import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { format } from 'date-fns';
import Badge from '../components/shared/Badge';

function toggleInSet(arr, id) {
  const s = new Set(arr);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  return Array.from(s);
}

function FilterMultiBox({
  title,
  footnote,
  accent,
  items,
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
  emptyText,
}) {
  const [filterText, setFilterText] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const blurTimer = useRef(null);

  useEffect(() => () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
  }, []);

  const nSel = selectedIds.length;
  const total = items.length;
  const q = filterText.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return items;
    return items.filter(it => it.name.toLowerCase().includes(q));
  }, [items, q]);

  const suggestions = useMemo(() => {
    if (!q) return [];
    return items
      .filter(it => it.name.toLowerCase().includes(q))
      .sort((a, b) => {
        const as = a.name.toLowerCase().startsWith(q) ? 0 : 1;
        const bs = b.name.toLowerCase().startsWith(q) ? 0 : 1;
        if (as !== bs) return as - bs;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 14);
  }, [items, q]);

  const inputPh = `Search ${title.toLowerCase()}…`;

  return (
    <div
      style={{
        borderRadius: 10,
        border: '1px solid #e3e8ee',
        background: '#fff',
        boxShadow: '0 2px 10px rgba(15, 116, 185, 0.07)',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div
        style={{
          padding: '14px 16px',
          borderTop: `3px solid ${accent}`,
          background: `linear-gradient(160deg, ${accent}12 0%, #ffffff 42%)`,
          borderBottom: '1px solid #eef1f4',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', letterSpacing: '-0.02em', lineHeight: 1.25 }}>{title}</div>
            <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4, lineHeight: 1.35 }}>
              {total === 0 ? 'No items loaded' : nSel === 0 ? `Using all ${total} — search or pick below` : `${nSel} selected · ${total - nSel} not included`}
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: accent,
                background: `${accent}1a`,
                padding: '4px 10px',
                borderRadius: 999,
                border: `1px solid ${accent}33`,
              }}
            >
              {nSel === 0 ? 'All' : `${nSel}`}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={onSelectAll} disabled={total === 0}>
                Select all
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => { setFilterText(''); onClear(); }}
                disabled={nSel === 0}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '10px 12px 12px',
          borderBottom: '1px solid #eef1f4',
          background: '#fff',
          zIndex: 2,
        }}
      >
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={filterText}
            onChange={e => { setFilterText(e.target.value); setSuggestOpen(true); }}
            onFocus={() => {
              if (blurTimer.current) clearTimeout(blurTimer.current);
              setSuggestOpen(true);
            }}
            onBlur={() => {
              blurTimer.current = setTimeout(() => setSuggestOpen(false), 200);
            }}
            placeholder={inputPh}
            autoComplete="off"
            spellCheck={false}
            aria-autocomplete="list"
            aria-expanded={suggestOpen && suggestions.length > 0}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 11px',
              border: `1px solid ${accent}40`,
              borderRadius: 8,
              fontSize: 13,
              outline: 'none',
            }}
          />
          {suggestOpen && q && suggestions.length > 0 && (
            <ul
              role="listbox"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 'calc(100% + 4px)',
                maxHeight: 220,
                overflowY: 'auto',
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                listStyle: 'none',
                padding: 4,
                margin: 0,
                zIndex: 20,
              }}
            >
              {suggestions.map(item => {
                const checked = selectedIds.includes(item.id);
                return (
                  <li key={item.id} role="option" aria-selected={checked}>
                    <button
                      type="button"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => onToggle(item.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '8px 10px',
                        border: 'none',
                        borderRadius: 6,
                        background: checked ? `${accent}12` : 'transparent',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: '#212529',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, fontWeight: checked ? 600 : 400 }}>{item.name}</span>
                      <span style={{ fontSize: 10, color: accent, fontWeight: 600, flexShrink: 0 }}>
                        {checked ? '✓ Selected' : '+ Add'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {suggestOpen && q && suggestions.length === 0 && total > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 'calc(100% + 4px)',
                padding: '12px 14px',
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                fontSize: 12,
                color: '#868e96',
                zIndex: 20,
              }}
            >
              No names match “{filterText.trim()}”.
            </div>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#868e96', marginTop: 6, lineHeight: 1.35 }}>
          Type to filter the list and see suggestions; click a row to select or unselect.
        </div>
      </div>

      <div
        style={{
          flex: 1,
          maxHeight: 240,
          minHeight: 120,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 8,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f4f8 100%)',
          borderBottom: '1px solid #eef1f4',
        }}
      >
        {items.length === 0 ? (
          <div style={{ padding: '28px 16px', textAlign: 'center', color: '#868e96', fontSize: 12, lineHeight: 1.5 }}>{emptyText}</div>
        ) : filteredItems.length === 0 ? (
          <div style={{ padding: '20px 12px', textAlign: 'center', color: '#868e96', fontSize: 12 }}>No matches in the list.</div>
        ) : (
          filteredItems.map((item, i) => {
            const checked = selectedIds.includes(item.id);
            return (
              <label
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 12px',
                  marginBottom: i < filteredItems.length - 1 ? 4 : 0,
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: checked ? 600 : 500,
                  color: checked ? '#212529' : '#495057',
                  background: checked ? '#fff' : i % 2 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.35)',
                  border: `1px solid ${checked ? `${accent}45` : 'rgba(0,0,0,0.06)'}`,
                  boxShadow: checked ? `0 1px 4px ${accent}20` : 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(item.id)}
                  style={{ width: 15, height: 15, accentColor: accent, flexShrink: 0, cursor: 'pointer' }}
                />
                <span style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>{item.name}</span>
              </label>
            );
          })
        )}
      </div>

      <div style={{ padding: '10px 16px', fontSize: 11, color: '#868e96', background: '#fff', lineHeight: 1.4 }}>{footnote}</div>
    </div>
  );
}

export default function JobReports() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientIds, setClientIds] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [statusId, setStatusId] = useState('');
  const [designerId, setDesignerId] = useState('');
  const [mode, setMode] = useState('');
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const { data: statuses = [] } = useQuery({ queryKey: ['statuses'], queryFn: () => api.get('/statuses').then(r => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });
  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => api.get('/categories').then(r => r.data) });
  const { data: designers = [] } = useQuery({
    queryKey: ['designers'],
    queryFn: () => api.get('/jobs/meta/designers').then(r => r.data),
  });

  const reportParams = useMemo(() => {
    const p = { date_from: dateFrom, date_to: dateTo };
    if (clientIds.length) p.client_ids = clientIds.join(',');
    if (categoryIds.length) p.category_ids = categoryIds.join(',');
    if (statusId) p.status_id = statusId;
    if (designerId) p.designer_id = designerId;
    if (mode) p.mode = mode;
    if (search.trim()) p.search = search.trim();
    if (includeArchived) p.include_archived = '1';
    return p;
  }, [dateFrom, dateTo, clientIds, categoryIds, statusId, designerId, mode, search, includeArchived]);

  const datesValid = Boolean(dateFrom && dateTo && dateFrom <= dateTo);

  const reportMut = useMutation({
    mutationFn: () => api.get('/jobs/report', { params: reportParams }).then(r => r.data),
  });

  const rows = reportMut.data?.data || [];

  const runReport = () => {
    if (!dateFrom || !dateTo) {
      toast.error('From date and To date are required.');
      return;
    }
    if (dateFrom > dateTo) {
      toast.error('From date must be on or before To date.');
      return;
    }
    reportMut.mutate();
  };

  const exportExcel = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('From date and To date are required.');
      return;
    }
    if (dateFrom > dateTo) {
      toast.error('From date must be on or before To date.');
      return;
    }
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    const q = new URLSearchParams();
    Object.entries(reportParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const token = localStorage.getItem('cms_token');
    try {
      const res = await fetch(`${base}/jobs/report/export?${q.toString()}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        toast.error(errBody.message || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition');
      let filename = `jobs-report_${dateFrom}_to_${dateTo}.xlsx`;
      const m = dispo && /filename="([^"]+)"/.exec(dispo);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  const card = { background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: '12px 14px', marginBottom: 12 };
  const label = { display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 };
  const inputSm = { padding: '6px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 };

  return (
    <div>
      <p style={{ fontSize: 12, color: '#6c757d', margin: '0 0 12px' }}>
        Set <strong>From</strong> and <strong>To</strong> dates (required). Leave client and category selections empty to include <strong>all</strong>. Use the checklists to limit to specific clients or categories.
      </p>

      <div style={card}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end', marginBottom: 14 }}>
          <div>
            <label style={label}>From date <span style={{ color: '#b64326' }}>*</span></label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputSm} required />
          </div>
          <div>
            <label style={label}>To date <span style={{ color: '#b64326' }}>*</span></label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputSm} required />
          </div>
          <div>
            <label style={label}>Status</label>
            <select value={statusId} onChange={e => setStatusId(e.target.value)} style={{ ...inputSm, minWidth: 140 }}>
              <option value="">All</option>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Designer</label>
            <select value={designerId} onChange={e => setDesignerId(e.target.value)} style={{ ...inputSm, minWidth: 140 }}>
              <option value="">All</option>
              {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Mode</label>
            <select value={mode} onChange={e => setMode(e.target.value)} style={{ ...inputSm, minWidth: 120 }}>
              <option value="">All</option>
              {['Internal', 'Retainer', 'Billable', 'Non - Billable'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={label}>Search</label>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Job no, client, project…"
              style={{ ...inputSm, width: '100%', maxWidth: 280 }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#343a40', cursor: 'pointer', marginBottom: 2 }}>
            <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} />
            Include archived jobs
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 18 }}>
          <FilterMultiBox
            title="Clients"
            footnote="Leave empty to include every client in the report."
            accent="#0f74b9"
            items={clients}
            selectedIds={clientIds}
            onToggle={id => setClientIds(toggleInSet(clientIds, id))}
            onSelectAll={() => setClientIds(clients.map(c => c.id))}
            onClear={() => setClientIds([])}
            emptyText="No active clients to show."
          />
          <FilterMultiBox
            title="Categories"
            footnote="Leave empty to include every category in the report."
            accent="#0c5460"
            items={categories}
            selectedIds={categoryIds}
            onToggle={id => setCategoryIds(toggleInSet(categoryIds, id))}
            onSelectAll={() => setCategoryIds(categories.map(cat => cat.id))}
            onClear={() => setCategoryIds([])}
            emptyText="No active categories to show."
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary btn-sm" disabled={!datesValid || reportMut.isPending} onClick={runReport}>
            {reportMut.isPending ? 'Loading…' : 'Run report'}
          </button>
          <button type="button" className="btn btn-outline btn-sm" disabled={!datesValid} onClick={exportExcel}>
            Export to Excel
          </button>
        </div>
      </div>

      {reportMut.isError && (
        <div style={{ color: '#b64326', fontSize: 12, marginBottom: 10 }}>{reportMut.error?.response?.data?.message || 'Could not load report.'}</div>
      )}

      {reportMut.isSuccess && (
        <div className="tbl-wrap">
          <div className="tbl-head-row">
            <span style={{ fontSize: 13, fontWeight: 600, color: '#343a40' }}>
              Results <span style={{ fontWeight: 400, fontSize: 11, color: '#6c757d' }}>({rows.length} jobs)</span>
            </span>
          </div>
          <div className="tbl-scroll">
            <table>
              <thead>
                <tr>
                  <th>Job No</th>
                  <th>Created</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Category</th>
                  <th>Due</th>
                  <th>Designer</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 28, color: '#6c757d' }}>No jobs match these filters.</td></tr>
                ) : rows.map(j => (
                  <tr key={j.id} className={j.is_overdue ? 'overdue' : ''}>
                    <td><span style={{ fontWeight: 700, color: j.is_overdue ? '#b64326' : '#0f74b9', fontFamily: 'monospace', fontSize: 11 }}>{j.job_no}</span></td>
                    <td style={{ color: '#6c757d', fontSize: 11, whiteSpace: 'nowrap' }}>{j.created_at ? format(new Date(j.created_at), 'dd MMM yyyy') : '—'}</td>
                    <td><strong>{j.client_name}</strong></td>
                    <td style={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.project_name}>{j.project_name}</td>
                    <td><span style={{ fontSize: 10, background: '#f1f3f5', padding: '2px 7px', borderRadius: 10, color: '#6c757d' }}>{j.category_name}</span></td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11, color: j.is_overdue ? '#b64326' : 'inherit' }}>
                      {j.due_date ? format(new Date(j.due_date), 'dd MMM yyyy') : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{j.designer_name || '—'}</td>
                    <td><Badge type="status" value={j.status_name} /></td>
                    <td><Badge type="mode" value={j.mode} /></td>
                    <td style={{ color: '#6c757d', fontSize: 11 }}>{j.invoice_no || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
